import dayjs from "dayjs";
import { CUSTOM_VALUE, DATE_SELECTION_OPTIONS, DateFormat } from "../../../../../../../../constants/dateFormat";
import { FilterChipData } from "../../../../../../../../Components/FilterShowcase/filterShowcase.models";
import {
    EscalationFilters,
    IAvailableCreatedBy,
    IAvailableEscalation,
    IAvailableEscalationStatus,
} from "../../../../../../../../Models/escalatedPageModels";
import {
    CREATED_AT_KEY,
    CREATED_BY_KEY,
    ESCALATION_TYPE_KEY,
    IS_MAIL_SENT_KEY,
    STATUS_KEY,
} from "../../../../../../escalatedPage.constants";

export type EscalationFilterChipData = FilterChipData<keyof EscalationFilters>;

interface BuildChipsParams {
    filters: EscalationFilters;
    devId: string | number;
    escalationList?: IAvailableEscalation[];
    statusList?: IAvailableEscalationStatus[];
    createdByList?: IAvailableCreatedBy[];
}

const getCreatedAtChipTitle = (filters: EscalationFilters): string | null => {
    const { createdAt, customStartDate, customEndDate } = filters;
    if (!createdAt) return null;
    if (createdAt === CUSTOM_VALUE) {
        if (customStartDate && customEndDate) {
            return `${dayjs(customStartDate).format(DateFormat.MM_DD_YYYY)} to ${dayjs(customEndDate).format(DateFormat.MM_DD_YYYY)}`;
        }
        return null;
    }
    const option = DATE_SELECTION_OPTIONS.find((opt) => opt.value === createdAt);
    return option?.label ?? createdAt;
};

const addChip = (
    result: EscalationFilterChipData[],
    config: {
        key: EscalationFilterChipData["key"];
        label: string;
        values?: string[];
        titles?: string | string[];
        mapValueToTitle?: (v: string) => string;
    }
): void => {
    const { key, label, values, titles, mapValueToTitle } = config;

    if (values?.length) {
        result.push({
            key,
            label,
            titles: mapValueToTitle ? values.map(mapValueToTitle) : values,
        });
        return;
    }

    if (titles) {
        result.push({ key, label, titles });
    }
};

export const buildEscalationFilterChips = ({
    filters,
    devId,
    escalationList,
    statusList,
    createdByList,
}: BuildChipsParams): EscalationFilterChipData[] => {
    const result: EscalationFilterChipData[] = [];

    addChip(result, {
        key: CREATED_BY_KEY,
        label: "Escalated By",
        values: filters.createdBy,
        mapValueToTitle: (val) =>
            val === `${devId}`
                ? "Me"
                : (createdByList?.find((user) => user.userId.toString() === val)?.userName.trim().split(" ")[0] ?? val),
    });

    addChip(result, {
        key: STATUS_KEY,
        label: "Status Selected",
        values: filters.status,
        mapValueToTitle: (val) =>
            statusList?.find((status) => status.id.toString() === val)?.name ?? val,
    });

    addChip(result, {
        key: ESCALATION_TYPE_KEY,
        label: "Selected Escalation Type",
        values: filters.escalationType,
        mapValueToTitle: (val) =>
            escalationList?.find((escalation) => escalation.escalationUid === val)?.escalationName ?? val,
    });

    if (filters.isMailSent) {
        addChip(result, { key: IS_MAIL_SENT_KEY, label: "Email Status", values: ["Sent"] });
    }

    const createdAtChipTitle = getCreatedAtChipTitle(filters);
    if (createdAtChipTitle) {
        addChip(result, { key: CREATED_AT_KEY, label: "Timestamp", titles: createdAtChipTitle });
    }

    return result;
};
